import { DefaultEventsMap } from "@socket.io/component-emitter";
import { Socket, io } from "socket.io-client";
import { getCookie } from "./CookieHelper";
import { COOKIE_ENUM, SOCKET_NAMESPACE } from "./enums";
import envConfig from "./env.config";
import { debugLog, sanitizeDiagnosticReason } from "./telemetrySanitization";

let socket: Socket<DefaultEventsMap, DefaultEventsMap> | undefined | null;
let franchiseSocket:
  | Socket<DefaultEventsMap, DefaultEventsMap>
  | undefined
  | null;
let hasLoggedSocketConnectionError = false;

/**
 * Blocks only genuine garbage ("undefined"/"null"/empty) from being sent in
 * socket query params — the values the backend decoded as "Invalid token
 * format" and retried on every reconnect. Any real bearer token is accepted:
 * not every environment issues JWT-shaped tokens, and requiring three
 * dot-separated segments here silently stopped the socket from connecting for
 * sessions that hold a valid opaque token.
 */
export const isValidSocketToken = (token?: string): token is string => {
  if (typeof token !== "string") {
    return false;
  }
  const trimmed = token.trim();

  return trimmed.length > 0 && trimmed !== "undefined" && trimmed !== "null";
};

export const claimSocketConnectionErrorLog = (): boolean => {
  if (hasLoggedSocketConnectionError) {
    return false;
  }
  hasLoggedSocketConnectionError = true;

  return true;
};

export const logSocketConnectionError = (error: unknown): void => {
  if (!claimSocketConnectionErrorLog()) {
    return;
  }
  const diagnostic = { reason: sanitizeDiagnosticReason(error) };
  if (process.env.NODE_ENV === "production") {
    console.warn("Restaurant socket connection failed", diagnostic);
  } else {
    debugLog("Restaurant socket connection failed", diagnostic);
  }
};

export const resetSocketConnectionErrorLog = (): void => {
  hasLoggedSocketConnectionError = false;
};

const buildAuthQuery = (idKey: string, id: string, token?: string) => {
  const query: Record<string, string> = { [idKey]: id };
  if (isValidSocketToken(token)) {
    query.token = token;
  }

  return query;
};

export const refreshSocketAuth = () => {
  const token = getCookie(COOKIE_ENUM.TOKEN);
  if (!isValidSocketToken(token)) {
    socket?.disconnect();
    franchiseSocket?.disconnect();

    return;
  }

  const restaurantId = socket?.io?.opts?.query?.restaurantId as
    | string
    | undefined;
  if (restaurantId) {
    socket!.io.opts.query = { restaurantId, token };
    if (socket!.connected) {
      socket!.disconnect().connect();
    } else {
      socket!.connect();
    }
  }

  const franchiseId = franchiseSocket?.io?.opts?.query?.franchiseId as
    | string
    | undefined;
  if (franchiseId) {
    franchiseSocket!.io.opts.query = { franchiseId, token };
    if (franchiseSocket!.connected) {
      franchiseSocket!.disconnect().connect();
    } else {
      franchiseSocket!.connect();
    }
  }
};

export const getSocket = (
  nameSpace: SOCKET_NAMESPACE,
  restaurantId: string
) => {
  const token = getCookie(COOKIE_ENUM.TOKEN);
  const canConnect = Boolean(restaurantId) && isValidSocketToken(token);

  if (socket && socket.io.opts.query?.restaurantId === restaurantId) {
    if (canConnect && !socket.connected) {
      socket.io.opts.query = buildAuthQuery(
        "restaurantId",
        restaurantId,
        token
      );
      socket.connect();
    }

    return socket;
  }

  if (socket) {
    debugLog("Replacing restaurant socket");
    socket.disconnect();
  }

  socket = io(envConfig.baseUrl + nameSpace, {
    transports: ["websocket", "polling"],
    query: buildAuthQuery("restaurantId", restaurantId, token),
    autoConnect: canConnect,
  });

  debugLog("Restaurant socket created");

  return socket;
};

export const getFranchiseSocket = (
  nameSpace: SOCKET_NAMESPACE,
  franchiseId: string
) => {
  const token = getCookie(COOKIE_ENUM.TOKEN);
  const canConnect = Boolean(franchiseId) && isValidSocketToken(token);

  if (
    franchiseSocket &&
    franchiseSocket.io.opts.query?.franchiseId === franchiseId
  ) {
    if (canConnect && !franchiseSocket.connected) {
      franchiseSocket.io.opts.query = buildAuthQuery(
        "franchiseId",
        franchiseId,
        token
      );
      franchiseSocket.connect();
    }

    return franchiseSocket;
  }

  if (franchiseSocket) {
    debugLog("Replacing franchise socket");
    franchiseSocket.disconnect();
  }

  franchiseSocket = io(envConfig.baseUrl + nameSpace, {
    transports: ["websocket", "polling"],
    query: buildAuthQuery("franchiseId", franchiseId, token),
    autoConnect: canConnect,
  });

  debugLog("Franchise socket created");

  return franchiseSocket;
};
